"use client";

/**
 * Schedule Renderer
 * Renders schedule preview in the canvas with three display modes:
 * accordion (default), cards, and carousel.
 * In public view, loads data via public actions and shows a detail modal on click.
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";

import { deleteScheduleItem } from "../../../../_logic/actions/schedule-actions";
import { ConfirmDeleteModal } from "../../../_shared/ConfirmDeleteModal";
import type { ScheduleItemRow } from "../../../../_logic/types/db-types";
import type { ScheduleComponent } from "../../../../_types";
import { useBuilderContext } from "../../../../_hooks/useBuilderContext";
import { useBuilderStore } from "../../../../_stores/builder-store";
import { useViewerRegistrations } from "../../../../_hooks/useViewerRegistrations";
import { groupItemsByDate, isTimedItem } from "../../../../_logic/utils/schedule-calendar-layout";
import { usePublicViewContext } from "../../../public/PublicViewContext";
import { useSchedulePublicData } from "../../../public/SchedulePublicDataProvider";
import { useToast } from "@/components/providers/FetchToastProvider";
import { InlineTipTapEdit } from "../_shared/InlineTipTapEdit";
import { SectionContainer } from "../_shared/SectionContainer";
import { AccordionView } from "./AccordionView";
import { AddToCalendarLink } from "./AddToCalendarLink";
import { AgendaView } from "./AgendaView";
import { CalendarView } from "./CalendarView";
import { CardsView } from "./CardsView";
import { CarouselView } from "./CarouselView";
import { ScheduleDetailModal } from "./ScheduleDetailModal";
import { ScheduleLabelFilter } from "./ScheduleLabelFilter";
import { SECTION_PROSE_CLASSES } from "../_shared/prose-section-text";

interface ScheduleRendererProps {
  component: ScheduleComponent;
  /** When true, hides delete buttons and skips store revision watching (used in public view) */
  isReadOnly?: boolean;
}

/** Renders a schedule section with items fetched from the database */
export function ScheduleRenderer({ component, isReadOnly }: ScheduleRendererProps): React.ReactElement {
  const { title, description } = component;
  const { eventId, rootSlug, parentPageId, primaryColor, timeZone } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const { showToast } = useToast();

  // Public view data — fetched once by SchedulePublicDataProvider and shared with the live banner
  const publicData = useSchedulePublicData();

  // Store reads (used in builder mode)
  const storeItems = useBuilderStore((s) => s.scheduleItems);
  const storeConfig = useBuilderStore((s) => s.scheduleConfig);
  const storeLabels = useBuilderStore((s) => s.scheduleLabels);
  const scheduleStatus = useBuilderStore((s) => s.scheduleStatus);
  const loadScheduleData = useBuilderStore((s) => s.loadScheduleData);
  const removeScheduleItem = useBuilderStore((s) => s.removeScheduleItem);
  const setSelectedId = useBuilderStore((s) => s.setSelectedId);
  const setFocusedField = useBuilderStore((s) => s.setFocusedField);

  // States — delete confirmation (builder only)
  const [deleteTarget, setDeleteTarget] = useState<ScheduleItemRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // States — detail modal (public view)
  const [selectedItem, setSelectedItem] = useState<ScheduleItemRow | null>(null);

  // States — label filter
  const [activeLabelId, setActiveLabelId] = useState<number | null>(null);

  // Viewer's confirmed registrations (`null` = no logged-in attendee). Loaded + kept in sync by the
  // shared hook; the calendar/agenda/etc. views match each item against slotIds/activityIds.
  const viewerRegistrations = useViewerRegistrations();

  // Effects — load schedule items/config/labels in builder mode. Public mode reads the data
  // fetched once by SchedulePublicDataProvider (see `publicData` below).
  useEffect(() => {
    if (isPublic) return;
    if (!isReadOnly && scheduleStatus === "idle") {
      loadScheduleData(eventId, parentPageId);
    }
  }, [isPublic, isReadOnly, scheduleStatus, eventId, parentPageId, loadScheduleData]);

  // Handlers
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const result = await deleteScheduleItem(deleteTarget.id);
    if (result.success) {
      showToast("Schedule item deleted", "success");
      removeScheduleItem(deleteTarget.id);
    } else {
      showToast(result.error || "Failed to delete schedule item", "error");
    }
    setIsDeleting(false);
    setDeleteTarget(null);
  }, [deleteTarget, showToast, removeScheduleItem]);

  // Derived values — use public data when in public view, store data otherwise
  const items = isPublic ? (publicData?.items ?? []) : storeItems;
  const config = isPublic ? (publicData?.config ?? null) : storeConfig;
  const labels = isPublic ? (publicData?.labels ?? []) : storeLabels;
  const isLoading = isPublic ? (publicData?.isLoading ?? true) : scheduleStatus === "loading";

  const displayStyle = config?.display_style ?? "accordion";
  const showTimeLabels = config?.show_time_labels ?? true;
  // The calendar lays out up to 4 day-columns side by side, so it needs more horizontal room
  // on desktop than the other (single-column) views; everything else stays at the default width.
  const sectionMaxWidth = displayStyle === "calendar" ? "6xl" : "4xl";
  const labelMap = useMemo(() => new Map(labels.map((l) => [l.id, l])), [labels]);

  // Derived values — filter items by selected label before grouping
  const filteredItems = useMemo(() => {
    if (!activeLabelId) return items;
    return items.filter((item) => item.label_ids.includes(activeLabelId));
  }, [items, activeLabelId]);
  const dateGroups = useMemo(() => groupItemsByDate(filteredItems), [filteredItems]);

  const handleBuilderItemClick = useCallback(
    (item: ScheduleItemRow) => {
      setSelectedId(component.id);
      setFocusedField(`schedule-item:${item.id}`);
    },
    [component.id, setSelectedId, setFocusedField]
  );

  const onItemClick = isPublic ? setSelectedItem : handleBuilderItemClick;

  // Derived values — focused full-page schedule entry point (public view only). The focused view is
  // a calendar time grid, so it's only offered when the schedule has at least one timed item. Opened
  // as a `?view=schedule` query param on the TOP-LEVEL page (rootSlug) — NOT a `/schedule` path,
  // which is a valid user subpage slug (e.g. yc-cancun's "schedule" subpage) and would be shadowed.
  // Shown on subpages too (schedule items are event-level): the link always targets `rootSlug`, so a
  // schedule section sitting on a subpage still links to the top-level focused view. The middleware
  // rewrites the short `/slug` URL to the long form, preserving the query string.
  const hasTimedItems = useMemo(() => items.some(isTimedItem), [items]);
  const showFocusedLink = isPublic && hasTimedItems && rootSlug.length > 0;
  const focusedScheduleHref = `/${rootSlug}?view=schedule`;

  // Derived values — public "Add to calendar" link (public view only), shown independently of the
  // focused-view link above: a calendar can be made public with no timed items yet, or vice versa.
  const publicCalendarUrl = isPublic ? (publicData?.publicCalendarUrl ?? null) : null;

  // Render
  return (
    <SectionContainer
      maxWidth={sectionMaxWidth}
      background={component.background}
      title={title || "Schedule"}
      componentId={component.id}
      titleFieldId="schedule-title"
    >
      <InlineTipTapEdit
        content={description || ""}
        className={`prose-base ${SECTION_PROSE_CLASSES}`}
        componentId={component.id}
        fieldId="schedule-description"
        field="description"
        placeholder="Click to add description..."
      />
      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex animate-pulse gap-3 rounded-lg bg-gray-100 p-4">
              <div className="h-4 w-20 rounded bg-gray-200" />
              <div className="flex-1">
                <div className="h-4 w-3/4 rounded bg-gray-200" />
                <div className="mt-2 h-3 w-1/2 rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? null : (
        <div className="flex flex-col gap-4">
          {/* Open full-page focused calendar view (new tab) + public "Add to calendar" link */}
          {(showFocusedLink || publicCalendarUrl) && (
            <div className="flex flex-wrap justify-center gap-2">
              {showFocusedLink && (
                <a
                  href={focusedScheduleHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  // Its own opaque white surface (bg-white), so text stays plain gray regardless
                  // of the section's own background.
                  className="inline-flex touch-manipulation items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  Open full view
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              )}
              {publicCalendarUrl && <AddToCalendarLink href={publicCalendarUrl} />}
            </div>
          )}

          {/* Label filter */}
          <ScheduleLabelFilter
            labels={labels}
            activeLabelId={activeLabelId}
            onChange={setActiveLabelId}
            primaryColor={primaryColor}
            className="justify-center"
          />

          {filteredItems.length === 0 ? (
            <p className="text-center font-medium text-[color:var(--lp-text,#6b7280)]">No items match this filter.</p>
          ) : (
            <>
              {displayStyle === "accordion" && (
                <AccordionView
                  dateGroups={dateGroups}
                  labels={labels}
                  labelMap={labelMap}
                  primaryColor={primaryColor}
                  onDelete={isReadOnly ? undefined : setDeleteTarget}
                  onItemClick={onItemClick}
                  timeZone={timeZone}
                  isPublic={isPublic}
                  viewerRegistrations={viewerRegistrations}
                />
              )}
              {displayStyle === "cards" && (
                <CardsView
                  dateGroups={dateGroups}
                  labels={labels}
                  labelMap={labelMap}
                  primaryColor={primaryColor}
                  onDelete={isReadOnly ? undefined : setDeleteTarget}
                  onItemClick={onItemClick}
                  timeZone={timeZone}
                  isPublic={isPublic}
                  viewerRegistrations={viewerRegistrations}
                />
              )}
              {displayStyle === "carousel" && (
                <CarouselView
                  dateGroups={dateGroups}
                  labels={labels}
                  labelMap={labelMap}
                  primaryColor={primaryColor}
                  onDelete={isReadOnly ? undefined : setDeleteTarget}
                  onItemClick={onItemClick}
                  timeZone={timeZone}
                  isPublic={isPublic}
                  viewerRegistrations={viewerRegistrations}
                />
              )}
              {displayStyle === "calendar" && (
                <CalendarView
                  dateGroups={dateGroups}
                  labelMap={labelMap}
                  primaryColor={primaryColor}
                  onDelete={isReadOnly ? undefined : setDeleteTarget}
                  onItemClick={onItemClick}
                  timeZone={timeZone}
                  isPublic={isPublic}
                  viewerRegistrations={viewerRegistrations}
                  showTimeLabels={showTimeLabels}
                />
              )}
              {displayStyle === "agenda" && (
                <AgendaView
                  dateGroups={dateGroups}
                  labels={labels}
                  labelMap={labelMap}
                  primaryColor={primaryColor}
                  onDelete={isReadOnly ? undefined : setDeleteTarget}
                  onItemClick={onItemClick}
                  timeZone={timeZone}
                  isPublic={isPublic}
                  viewerRegistrations={viewerRegistrations}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal (builder only) */}
      {!isReadOnly && (
        <ConfirmDeleteModal
          isOpen={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Schedule Item"
          description={`Delete "${deleteTarget?.title}"?`}
          isDeleting={isDeleting}
        />
      )}

      {/* Detail Modal (public view) */}
      {selectedItem && (
        <ScheduleDetailModal
          item={selectedItem}
          labels={labels}
          primaryColor={primaryColor}
          showTimeLabels={displayStyle !== "calendar" || showTimeLabels}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </SectionContainer>
  );
}
